import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { INotificationsListMeta, INotification } from 'types/types';

export interface NotificationsState {
  notifications: INotification[];
  isFirstTimeLoading: boolean;
  isNextPageLoading: boolean;
  notificationsListMeta: INotificationsListMeta;
}

const initialNotificationsListMeta = {
  page: 1,
  size: 20,
  total: 0,
  totalPages: 1,
  unreadCount: 0,
};

const initialState: NotificationsState = {
  notifications: [],
  isFirstTimeLoading: false,
  isNextPageLoading: false,
  notificationsListMeta: initialNotificationsListMeta,
};

export const name = 'notifications';

const notificationsSlice = createSlice({
  name,
  initialState,
  reducers: {
    setNotifications(state, { payload }: PayloadAction<INotification[]>) {
      state.notifications = payload;
      state.isFirstTimeLoading = false;
    },
    setNotificationsListMeta(
      state,
      { payload }: PayloadAction<INotificationsListMeta>
    ) {
      state.notificationsListMeta = payload;
    },
    setNotificationsPage(state, { payload }: PayloadAction<number>) {
      state.notificationsListMeta.page = payload;
    },
    setIsFirstTimeLoading(state, { payload }: PayloadAction<boolean>) {
      state.isFirstTimeLoading = payload;
    },
    setIsNextPageLoading(state, { payload }: PayloadAction<boolean>) {
      state.isNextPageLoading = payload;
    },

    addNotificationInList(state, { payload }: PayloadAction<INotification>) {
      state.notifications = [payload, ...state.notifications];
      state.notificationsListMeta.unreadCount += 1;
    },
    updateNotificationInList(state, { payload }: PayloadAction<INotification>) {
      const notification = state.notifications.find(
        notification => notification.id === payload.id
      );
      if (notification) {
        Object.assign(notification, payload);
      }
    },

    markNotificationAsRead(state, { payload }: PayloadAction<string>) {
      const notification = state.notifications.find(
        notification => notification.id === payload
      );

      if (notification) {
        notification.readAt = new Date().toISOString();
        if (state.notificationsListMeta.unreadCount > 0)
          state.notificationsListMeta.unreadCount -= 1;
      }
    },

    markAllNotificationsAsRead(state) {
      const now = new Date().toISOString();

      state.notifications.forEach(notification => {
        if (!notification.readAt) {
          notification.readAt = now;
        }
      });

      state.notificationsListMeta.unreadCount = 0;
    },

    removeNotificationFromList(state, { payload }: PayloadAction<string>) {
      const index = state.notifications.findIndex(
        notification => notification.id === payload
      );
      if (index !== -1) {
        if (!state.notifications[index].readAt) {
          state.notificationsListMeta.unreadCount -= 1;
        }

        state.notifications.splice(index, 1);
      }
    },

    removeAllNotificationsFromList(state) {
      state.notifications = [];
      state.notificationsListMeta = initialNotificationsListMeta;
    },
  },
});

export const {
  setNotifications,
  setNotificationsListMeta,
  setNotificationsPage,
  setIsFirstTimeLoading,
  setIsNextPageLoading,
  addNotificationInList,
  updateNotificationInList,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  removeNotificationFromList,
  removeAllNotificationsFromList,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;
